import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Form, Modal, Button, OverlayTrigger, Tooltip, Toast, ToastContainer } from 'react-bootstrap';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import {
    getHydraulicConfig,
    updateHydraulicConfig,
    getDefaultConsumptionRange,
    getWaterConsumption,
    HYDRAULIC_CONFIG_PLACEHOLDERS,
} from '../../../services/waterConsumption/waterConsumptionService';

// ---------------------------------------------------------------------------
// Requerimiento 3 (Consumo de agua por cama/bloque). La configuración
// hidráulica y el consumo diario (pulsos + litros ya calculados con la
// configuración vigente en cada fecha) vienen del backend a través de
// waterConsumptionService.js.
// ---------------------------------------------------------------------------

const BLOCK_NAME = 'Bloque';
const LITERS_PER_M3 = 1000;
// La vista "Diario" usa un eje categórico (no de tiempo), así que con periodos
// largos se amontonan cientos de barras y etiquetas ilegibles. Se limita a
// periodos cortos, donde además tiene sentido (detectar el día raro sin riego).
const MAX_DAYS_FOR_DAILY_VIEW = 60;

// Borrador vacío para el formulario cuando el bloque/cultivo todavía no tiene
// configuración hidráulica guardada — los campos quedan vacíos (no en 0), para
// que solo se vea el placeholder de referencia hasta que el usuario escriba
// sus propios valores.
const EMPTY_CONFIG_DRAFT = {
    pulseDurationMinutes: '',
    flowRatePerDripperCcMin: '',
    drippersPerMeter: '',
    bedLengthMeters: '',
    bedsPerLot: '',
    dripLinesPerBed: '',
};

function toDateInputValue(date) {
    const pad = (n) => (n < 10 ? '0' + n : n);
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// `new Date("YYYY-MM-DD")` se interpreta como medianoche UTC — al mostrarla en
// una zona horaria negativa como Colombia (UTC-5), se corre un día hacia
// atrás. Se parsea manualmente como fecha local en vez de dejar que el string
// se interprete como UTC.
function parseDateInputValue(value) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
}

function formatDateEs(date) {
    return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatNumber(value) {
    return Math.round(value).toLocaleString('es-CO');
}

// Para cifras con decimales fijos (ej. "35,10"), usando coma como separador
// decimal (convención colombiana) en vez de `.toFixed()`, que siempre usa punto.
function formatDecimal(value, decimals = 2) {
    return value.toLocaleString('es-CO', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

const WaterConsumption = ({ cropId, readOnly = false }) => {
    const [loading, setLoading] = useState(true);
    // `config` es lo ya guardado y lo único que alimenta los cálculos/gráficas.
    // `draftConfig` es lo que se edita en el modal — un cambio ahí no debe
    // afectar nada hasta que se confirme con "Guardar" (para eso es el botón).
    const [config, setConfig] = useState(EMPTY_CONFIG_DRAFT);
    const [draftConfig, setDraftConfig] = useState(EMPTY_CONFIG_DRAFT);
    const [isConfigured, setIsConfigured] = useState(false);
    const [entries, setEntries] = useState([]);
    const [showConfigModal, setShowConfigModal] = useState(false);
    // Modal de confirmación al guardar una config cuando ya existía una
    // anterior: decide si el cambio corrige también los días ya calculados.
    const [showApplyChoiceModal, setShowApplyChoiceModal] = useState(false);
    const [chartMode, setChartMode] = useState('acumulado'); // 'acumulado' | 'diario'
    const [unit, setUnit] = useState('m3'); // 'L' | 'm3'

    // `rangeStart`/`rangeEnd` son el rango confirmado — el único que dispara
    // la petición de consumo. `draftRangeStart`/`draftRangeEnd` es lo que se
    // edita en los inputs del popover; un cambio ahí no debe disparar nada
    // hasta que se confirme con "Aplicar" (mismo patrón que la config).
    const [rangeStart, setRangeStart] = useState('');
    const [rangeEnd, setRangeEnd] = useState('');
    const [draftRangeStart, setDraftRangeStart] = useState('');
    const [draftRangeEnd, setDraftRangeEnd] = useState('');
    // Límites reales de datos disponibles (primer pulso registrado - hoy),
    // usados para acotar el selector de fechas.
    const [availableRange, setAvailableRange] = useState({ start: '', end: '' });
    const [showDatePicker, setShowDatePicker] = useState(false);
    // Feedback visual al guardar la configuración — antes el modal solo se
    // cerraba sin indicar si el guardado fue exitoso o falló.
    const [saveToast, setSaveToast] = useState({ show: false, variant: 'success', message: '' });

    useEffect(() => {
        const fetchInitial = async () => {
            setLoading(true);
            const [hydraulicConfig, defaultRange] = await Promise.all([
                getHydraulicConfig(cropId),
                getDefaultConsumptionRange(cropId),
            ]);
            if (hydraulicConfig) {
                setConfig(hydraulicConfig);
                setDraftConfig(hydraulicConfig);
                setIsConfigured(true);
            } else {
                setConfig(EMPTY_CONFIG_DRAFT);
                setDraftConfig(EMPTY_CONFIG_DRAFT);
                setIsConfigured(false);
                // Quien solo ve el cultivo no puede configurarlo
                if (!readOnly) setShowConfigModal(true);
            }
            setAvailableRange({ start: defaultRange.defaultStart, end: defaultRange.defaultEnd });
            setRangeStart(defaultRange.defaultStart);
            setRangeEnd(defaultRange.defaultEnd);
            setDraftRangeStart(defaultRange.defaultStart);
            setDraftRangeEnd(defaultRange.defaultEnd);
            setLoading(false);
        };
        fetchInitial();
    }, [cropId, readOnly]);

    // El backend ya calcula pulsos y litros por día (con la configuración
    // vigente en cada fecha), así que solo se vuelve a pedir cuando cambia el
    // cultivo o el rango de fechas seleccionado.
    useEffect(() => {
        if (!rangeStart || !rangeEnd) return;
        const fetchEntries = async () => {
            const data = await getWaterConsumption(cropId, rangeStart, rangeEnd);
            setEntries(
                data.map((entry) => ({
                    date: parseDateInputValue(entry.date),
                    pulseCount: entry.pulseCount,
                    litersLot: entry.liters ?? 0,
                }))
            );
        };
        fetchEntries();
    }, [cropId, rangeStart, rangeEnd]);

    // Al abrir el modal, el borrador siempre parte de lo ya guardado — así, si
    // la vez anterior se cerró sin guardar, no quedan cambios a medias.
    const handleOpenConfigModal = () => {
        setDraftConfig(config);
        setShowConfigModal(true);
    };

    const handleConfigChange = (field) => (event) => {
        const raw = event.target.value;
        if (raw === '') {
            setDraftConfig((prev) => ({ ...prev, [field]: '' }));
            return;
        }
        const value = parseFloat(raw);
        setDraftConfig((prev) => ({ ...prev, [field]: isNaN(value) ? '' : value }));
    };

    // Se valida explícitamente la lista de campos del formulario (no
    // Object.values(draftConfig) completo) porque el objeto que devuelve el
    // backend trae además `validFrom` (fecha de vigencia, no un campo del
    // formulario) — iterar sobre todos los valores hacía que la validación
    // fallara siempre al toparse con un string no numérico.
    const isDraftComplete = Object.keys(EMPTY_CONFIG_DRAFT).every(
        (field) => draftConfig[field] !== '' && draftConfig[field] != null && !isNaN(draftConfig[field])
    );

    // Cerrar (X, backdrop, Escape) siempre se permite y descarta el borrador —
    // el usuario puede estar solo explorando y no forzarlo a configurar de una vez.
    const handleDismissConfigModal = () => {
        setDraftConfig(config);
        setShowConfigModal(false);
    };

    // Guardar sí valida: si falta algo, se queda abierto mostrando el error.
    // Si ya existía una configuración previa, primero se pregunta si el
    // cambio debe corregir también los días ya calculados (versionado por
    // fecha de vigencia en el backend) o aplicar solo desde hoy.
    const handleSaveConfig = () => {
        if (!isDraftComplete) return;
        if (isConfigured) {
            setShowApplyChoiceModal(true);
        } else {
            performSaveConfig(false);
        }
    };

    const performSaveConfig = async (applyToHistory) => {
        try {
            const saved = await updateHydraulicConfig(cropId, draftConfig, applyToHistory);
            setConfig(saved);
            setDraftConfig(saved);
            setIsConfigured(true);
            setShowApplyChoiceModal(false);
            setShowConfigModal(false);
            setSaveToast({ show: true, variant: 'success', message: 'Configuración guardada correctamente.' });
            if (rangeStart && rangeEnd) {
                const data = await getWaterConsumption(cropId, rangeStart, rangeEnd);
                setEntries(
                    data.map((entry) => ({
                        date: parseDateInputValue(entry.date),
                        pulseCount: entry.pulseCount,
                        litersLot: entry.liters ?? 0,
                    }))
                );
            }
        } catch (error) {
            // Se deja el modal de configuración abierto (con los datos ya
            // escritos) para que el usuario pueda reintentar sin volver a
            // llenar el formulario; solo se cierra el de elección de vigencia.
            setShowApplyChoiceModal(false);
            setSaveToast({
                show: true,
                variant: 'danger',
                message: 'No se pudo guardar la configuración. Intenta de nuevo.',
            });
        }
    };

    // Litros por pulso (cama) = duración del pulso (min) x caudal por gotero (L/min)
    //                           x goteros por metro x longitud de la cama (m)
    //                           x líneas de goteo por cama
    const litersPerPulsePerBed = useMemo(() => {
        if (!config) return 0;
        const flowRateLMin = config.flowRatePerDripperCcMin / 1000;
        const drippersPerLine = config.drippersPerMeter * config.bedLengthMeters;
        return config.pulseDurationMinutes * flowRateLMin * drippersPerLine * config.dripLinesPerBed;
    }, [config]);

    const litersPerPulsePerLot = litersPerPulsePerBed * (config?.bedsPerLot || 0);

    // El backend ya devuelve, para el rango seleccionado, los litros del
    // bloque por día calculados con la configuración vigente en cada fecha
    // (que puede no ser la config actual, si hubo cambios versionados).
    const periodEntries = entries;

    const periodDays = periodEntries.length;
    const periodTotalLiters = periodEntries.reduce((sum, d) => sum + d.litersLot, 0);
    const periodTotalPulses = periodEntries.reduce((sum, d) => sum + d.pulseCount, 0);
    const avgDailyPulses = periodDays > 0 ? periodTotalPulses / periodDays : 0;
    const avgDailyLiters = periodDays > 0 ? periodTotalLiters / periodDays : 0;
    const avgWeeklyLiters = avgDailyLiters * 7;
    const avgMonthlyLiters = avgDailyLiters * 30;

    const canShowDaily = periodDays <= MAX_DAYS_FOR_DAILY_VIEW;
    useEffect(() => {
        if (!canShowDaily && chartMode === 'diario') {
            setChartMode('acumulado');
        }
    }, [canShowDaily, chartMode]);

    const displayValue = (liters) => unit === 'm3' ? liters / LITERS_PER_M3 : liters;

    // Los valores en litros suelen tener muchos más dígitos que en m³ (miles vs.
    // unidades), así que necesitan más espacio a la izquierda para no encimarse
    // con el título del eje; además se separan los miles con punto (convención
    // colombiana: punto para miles, coma para decimales), igual que las fechas
    // ya usan el locale 'es-CO'.
    const axisLeftMargin = unit === 'L' ? 85 : 60;
    const axisLeftLegendOffset = unit === 'L' ? -70 : -50;
    const formatAxisTick = (value) =>
        value.toLocaleString('es-CO', { maximumFractionDigits: unit === 'm3' ? 2 : 0 });
    const unitLabel = unit === 'm3' ? 'm³' : 'L';

    // Serie acumulada (para el modo "Acumulado")
    const cumulativeChartData = useMemo(() => {
        let running = 0;
        const points = periodEntries.map((d) => {
            running += d.litersLot;
            return { x: toDateInputValue(d.date), y: Math.round(displayValue(running) * 100) / 100 };
        });
        return [{ id: 'Acumulado', data: points }];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodEntries, unit]);

    // Serie diaria (para el modo "Diario"). Se conserva el objeto Date original
    // (dateObj) para el tooltip, en vez de re-parsear el string "fecha" — un
    // string tipo "YYYY-MM-DD" se interpreta como UTC y puede desfasarse un día
    // al mostrarse en una zona horaria negativa como Colombia.
    const dailyChartData = periodEntries.map((d) => ({
        fecha: toDateInputValue(d.date),
        dateObj: d.date,
        litros: Math.round(displayValue(d.litersLot) * 100) / 100,
    }));

    // "fecha" es un eje categórico (band scale), no de tiempo — 'every N days'
    // (usado en la gráfica acumulada) no aplica acá. Se calcula explícitamente
    // un subconjunto de fechas para que las etiquetas no se amontonen.
    const dailyTickValues = dailyChartData.length > 15
        ? dailyChartData
            .filter((_, index) => index % Math.ceil(dailyChartData.length / 15) === 0)
            .map((d) => d.fecha)
        : undefined;

    const renderConfigModal = () => (
        <Modal show={showConfigModal} onHide={handleDismissConfigModal} centered>
            <Modal.Header closeButton>
                <Modal.Title>Configuración Hidráulica — {BLOCK_NAME}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {!isConfigured && (
                    <p className="text-sm text-gray-500">
                        Este bloque todavía no tiene parámetros hidráulicos registrados.
                        Completa todos los campos para poder ver el consumo de agua estimado.
                    </p>
                )}
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label className="text-sm font-medium">Duración del pulso (min)</Form.Label>
                        <Form.Control
                            type="number"
                            step="0.5"
                            placeholder={String(HYDRAULIC_CONFIG_PLACEHOLDERS.pulseDurationMinutes)}
                            value={draftConfig.pulseDurationMinutes}
                            onChange={handleConfigChange('pulseDurationMinutes')}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="text-sm font-medium">Caudal por gotero (cc/min)</Form.Label>
                        <Form.Control
                            type="number"
                            step="0.5"
                            placeholder={String(HYDRAULIC_CONFIG_PLACEHOLDERS.flowRatePerDripperCcMin)}
                            value={draftConfig.flowRatePerDripperCcMin}
                            onChange={handleConfigChange('flowRatePerDripperCcMin')}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="text-sm font-medium">Goteros por metro</Form.Label>
                        <Form.Control
                            type="number"
                            step="1"
                            placeholder={String(HYDRAULIC_CONFIG_PLACEHOLDERS.drippersPerMeter)}
                            value={draftConfig.drippersPerMeter}
                            onChange={handleConfigChange('drippersPerMeter')}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="text-sm font-medium">Longitud de cada cama (m)</Form.Label>
                        <Form.Control
                            type="number"
                            step="0.5"
                            placeholder={String(HYDRAULIC_CONFIG_PLACEHOLDERS.bedLengthMeters)}
                            value={draftConfig.bedLengthMeters}
                            onChange={handleConfigChange('bedLengthMeters')}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="text-sm font-medium">Líneas de goteo por cama</Form.Label>
                        <Form.Control
                            type="number"
                            step="1"
                            placeholder={String(HYDRAULIC_CONFIG_PLACEHOLDERS.dripLinesPerBed)}
                            value={draftConfig.dripLinesPerBed}
                            onChange={handleConfigChange('dripLinesPerBed')}
                        />
                    </Form.Group>
                    <Form.Group className="mb-0">
                        <Form.Label className="text-sm font-medium">Número de camas por bloque</Form.Label>
                        <Form.Control
                            type="number"
                            step="1"
                            placeholder={String(HYDRAULIC_CONFIG_PLACEHOLDERS.bedsPerLot)}
                            value={draftConfig.bedsPerLot}
                            onChange={handleConfigChange('bedsPerLot')}
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer className="flex-column align-items-stretch">
                {!isDraftComplete && (
                    <p className="text-xs text-red-500 mb-2 text-center">
                        Completa todos los campos para guardar.
                    </p>
                )}
                <Button variant="primary" onClick={handleSaveConfig} disabled={!isDraftComplete}>
                    Guardar
                </Button>
            </Modal.Footer>
        </Modal>
    );

    const renderSaveToast = () => (
        <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1060 }}>
            <Toast
                show={saveToast.show}
                onClose={() => setSaveToast((prev) => ({ ...prev, show: false }))}
                delay={4000}
                autohide
                bg={saveToast.variant}
            >
                <Toast.Body className="text-white">
                    {saveToast.message}
                </Toast.Body>
            </Toast>
        </ToastContainer>
    );

    const renderApplyChoiceModal = () => (
        <Modal show={showApplyChoiceModal} onHide={() => setShowApplyChoiceModal(false)} centered>
            <Modal.Header closeButton>
                <Modal.Title>¿Desde cuándo aplica este cambio?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-sm text-gray-600 mb-0">
                    Ya existe una configuración guardada para este {BLOCK_NAME.toLowerCase()}. Puedes aplicar el
                    cambio solo desde hoy en adelante, o corregir también los consumos ya calculados con la
                    configuración anterior.
                </p>
            </Modal.Body>
            <Modal.Footer className="flex-column align-items-stretch gap-2">
                <Button variant="outline-primary" onClick={() => performSaveConfig(false)}>
                    Aplicar solo desde hoy
                </Button>
                <Button variant="primary" onClick={() => performSaveConfig(true)}>
                    Corregir también el histórico
                </Button>
            </Modal.Footer>
        </Modal>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20 text-gray-400">
                Cargando información de consumo de agua...
            </div>
        );
    }

    if (!isConfigured) {
        return (
            <div className="space-y-4">
                {!readOnly && (
                    <div className="flex items-center justify-end">
                        <button
                            onClick={handleOpenConfigModal}
                            title="Configurar parámetros hidráulicos"
                            className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 shadow-sm hover:bg-gray-50 text-gray-500"
                        >
                            <span className="material-icons-outlined text-lg">settings</span>
                        </button>
                    </div>
                )}
                <div className="flex flex-col items-center justify-center text-center py-16 text-gray-500">
                    <span className="material-icons-outlined text-5xl text-gray-300 mb-3">settings</span>
                    <p className="max-w-md">
                        {readOnly
                            ? 'Este bloque aún no tiene configurados los parámetros hidráulicos.'
                            : 'Aún no has configurado los parámetros hidráulicos de este bloque. Haz clic en el ícono de ajustes para completarlos y ver el consumo de agua estimado.'}
                    </p>
                </div>
                {renderConfigModal()}
                {renderApplyChoiceModal()}
                {renderSaveToast()}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Barra superior: rango de fechas + ícono de ajustes */}
            <div className="flex items-center justify-end gap-2">
                {!readOnly && (
                    <button
                        onClick={handleOpenConfigModal}
                        title="Configurar parámetros hidráulicos"
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 shadow-sm hover:bg-gray-50 text-gray-500"
                    >
                        <span className="material-icons-outlined text-lg">settings</span>
                    </button>
                )}

                <div className="relative">
                    <button
                        onClick={() => {
                            // El borrador siempre parte del rango ya confirmado al
                            // abrir — así, si la vez anterior se cerró sin aplicar,
                            // no quedan fechas a medio elegir.
                            setDraftRangeStart(rangeStart);
                            setDraftRangeEnd(rangeEnd);
                            setShowDatePicker((v) => !v);
                        }}
                        className="flex items-center gap-2 bg-white border border-gray-200 shadow-sm rounded-full px-4 py-2 text-sm text-gray-700"
                    >
                        <span className="material-icons-outlined text-base text-gray-400">calendar_today</span>
                        {formatDateEs(parseDateInputValue(rangeStart))} - {formatDateEs(parseDateInputValue(rangeEnd))}
                        <span className="material-icons-outlined text-base text-gray-400">expand_more</span>
                    </button>
                    {showDatePicker && (
                        <div className="absolute right-0 mt-2 bg-white border border-gray-200 shadow-lg rounded-xl p-3 z-10 flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                                <Form.Control
                                    type="date"
                                    size="sm"
                                    value={draftRangeStart}
                                    min={availableRange.start}
                                    max={draftRangeEnd}
                                    onChange={(e) => setDraftRangeStart(e.target.value)}
                                    style={{ width: 150 }}
                                />
                                <span className="text-gray-400 text-sm">–</span>
                                <Form.Control
                                    type="date"
                                    size="sm"
                                    value={draftRangeEnd}
                                    min={draftRangeStart}
                                    max={availableRange.end}
                                    onChange={(e) => setDraftRangeEnd(e.target.value)}
                                    style={{ width: 150 }}
                                />
                            </div>
                            <Button
                                size="sm"
                                variant="primary"
                                className="align-self-end"
                                onClick={() => {
                                    setRangeStart(draftRangeStart);
                                    setRangeEnd(draftRangeEnd);
                                    setShowDatePicker(false);
                                }}
                            >
                                Aplicar
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm rounded-lg px-4 py-2 flex items-center gap-2">
                <span className="material-icons-outlined text-base">info</span>
                Datos calculados con base en la configuración hidráulica y los pulsos registrados.
            </div>

            {/* Tarjetas de indicadores */}
            <Row className="g-3">
                <Col md={3} sm={6}>
                    <Card className="border-0 shadow-sm rounded-xl h-100">
                        <Card.Body className="p-4 flex items-center gap-3">
                            <div className="w-11 h-11 rounded-full bg-cyan-50 flex items-center justify-center flex-shrink-0">
                                <span className="material-icons-outlined text-cyan-600">monitor_heart</span>
                            </div>
                            <div>
                                <div className="text-xs text-gray-400">Pulsos diarios (promedio)</div>
                                <div className="text-xl font-bold text-gray-800">{formatDecimal(avgDailyPulses)}</div>
                                <div className="text-[10px] text-gray-400">pulsos/día</div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} sm={6}>
                    <Card className="border-0 shadow-sm rounded-xl h-100">
                        <Card.Body className="p-4 flex items-center gap-3">
                            <div className="w-11 h-11 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                                <span className="material-icons-outlined text-green-600">water_drop</span>
                            </div>
                            <div>
                                <div className="text-xs text-gray-400">Litros por pulso (cama)</div>
                                <div className="text-xl font-bold text-gray-800">{formatDecimal(litersPerPulsePerBed)} L</div>
                                <div className="text-[10px] text-gray-400">por cama</div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} sm={6}>
                    <Card className="border-0 shadow-sm rounded-xl h-100">
                        <Card.Body className="p-4 flex items-center gap-3">
                            <div className="w-11 h-11 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                                <span className="material-icons-outlined text-blue-600">water_drop</span>
                            </div>
                            <div>
                                <div className="text-xs text-gray-400">Litros por pulso ({BLOCK_NAME.toLowerCase()})</div>
                                <div className="text-xl font-bold text-gray-800">{formatNumber(litersPerPulsePerLot)} L</div>
                                <div className="text-[10px] text-gray-400">por bloque ({config.bedsPerLot.toLocaleString('es-CO')} camas)</div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3} sm={6}>
                    <Card className="border-0 shadow-sm rounded-xl h-100">
                        <Card.Body className="p-4 flex items-center gap-3">
                            <div className="w-11 h-11 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
                                <span className="material-icons-outlined text-purple-600">view_stream</span>
                            </div>
                            <div>
                                <div className="text-xs text-gray-400">Líneas de goteo por cama</div>
                                <div className="text-xl font-bold text-gray-800">{config.dripLinesPerBed}</div>
                                <div className="text-[10px] text-gray-400">líneas</div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="g-2">
                {/* Gráfica */}
                <Col lg={8}>
                    <Card className="border-0 shadow-sm rounded-xl">
                        <Card.Header className="bg-white border-b border-gray-100 py-3">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div>
                                    <h5 className="mb-0 text-gray-800 font-bold">Consumo de agua acumulado en el {BLOCK_NAME}</h5>
                                    <span className="text-xs text-gray-400">
                                        {formatDateEs(parseDateInputValue(rangeStart))} al {formatDateEs(parseDateInputValue(rangeEnd))} ({periodDays} días)
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Form.Select
                                        size="sm"
                                        style={{ width: 170 }}
                                        value={chartMode}
                                        onChange={(e) => setChartMode(e.target.value)}
                                    >
                                        <option value="acumulado">Acumulado</option>
                                        <option value="diario" disabled={!canShowDaily}>
                                            {canShowDaily ? 'Diario' : `Diario (máx. ${MAX_DAYS_FOR_DAILY_VIEW} días)`}
                                        </option>
                                    </Form.Select>
                                    <div className="btn-group btn-group-sm">
                                        <button
                                            className={`btn ${unit === 'L' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            onClick={() => setUnit('L')}
                                        >
                                            L
                                        </button>
                                        <button
                                            className={`btn ${unit === 'm3' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            onClick={() => setUnit('m3')}
                                        >
                                            m³
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-4 pb-2">
                            <div style={{ height: 300 }}>
                                {periodDays === 0 ? (
                                    <div className="flex items-center justify-center h-full text-gray-400">
                                        No hay pulsos registrados en el periodo seleccionado.
                                    </div>
                                ) : chartMode === 'acumulado' ? (
                                    // Nivo usa useUTC:true por defecto en escalas de tiempo — como el
                                    // string "YYYY-MM-DD" se construye en hora local (toDateInputValue),
                                    // parsearlo como UTC y mostrarlo en una zona negativa (Bogotá, UTC-5)
                                    // corre la fecha un día hacia atrás, el mismo bug ya corregido en el
                                    // resto del componente. Se fuerza useUTC:false para que el parseo y el
                                    // formateo de ticks/tooltip usen la misma hora local.
                                    <ResponsiveLine
                                        data={cumulativeChartData}
                                        margin={{ top: 20, right: 40, bottom: 65, left: axisLeftMargin }}
                                        xScale={{ type: 'time', format: '%Y-%m-%d', precision: 'day', useUTC: false }}
                                        xFormat="time:%Y-%m-%d"
                                        yScale={{ type: 'linear', min: 0, max: 'auto' }}
                                        axisBottom={{
                                            format: '%b %y',
                                            tickValues: 'every 1 month',
                                            tickRotation: 60,
                                            legend: 'Fecha',
                                            legendOffset: 55,
                                            legendPosition: 'middle',
                                        }}
                                        axisLeft={{
                                            legend: unit === 'm3' ? 'm³' : 'Litros (L)',
                                            legendOffset: axisLeftLegendOffset,
                                            legendPosition: 'middle',
                                            format: formatAxisTick,
                                        }}
                                        colors={['#3b82f6']}
                                        lineWidth={3}
                                        enableArea={true}
                                        areaOpacity={0.1}
                                        enablePoints={false}
                                        enableSlices="x"
                                        sliceTooltip={({ slice }) => (
                                            <div style={{ background: 'white', padding: '9px 12px', border: '1px solid #ccc' }}>
                                                {slice.points.map((point, index) => (
                                                    <div key={index}>
                                                        <strong>Fecha:</strong> {point.data.xFormatted} <br />
                                                        <strong>Acumulado:</strong> {formatAxisTick(point.data.y)} {unitLabel}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        useMesh={true}
                                        animate={false}
                                    />
                                ) : (
                                    <ResponsiveBar
                                        data={dailyChartData}
                                        keys={['litros']}
                                        indexBy="fecha"
                                        margin={{ top: 20, right: 20, bottom: 90, left: axisLeftMargin }}
                                        padding={0.3}
                                        colors={['#3b82f6']}
                                        axisBottom={{
                                            tickRotation: 60,
                                            legend: 'Fecha',
                                            legendPosition: 'middle',
                                            legendOffset: 75,
                                            tickValues: dailyTickValues,
                                        }}
                                        axisLeft={{
                                            legend: unit === 'm3' ? 'm³ / bloque' : 'Litros (L) / bloque',
                                            legendPosition: 'middle',
                                            legendOffset: axisLeftLegendOffset,
                                            format: formatAxisTick,
                                        }}
                                        enableLabel={false}
                                        tooltip={({ data }) => (
                                            <div style={{ background: 'white', padding: '9px 12px', border: '1px solid #ccc' }}>
                                                <strong>Fecha:</strong> {toDateInputValue(data.dateObj)} <br />
                                                <strong>Consumo:</strong> {formatAxisTick(data.litros)} {unitLabel}
                                            </div>
                                        )}
                                        animate={false}
                                    />
                                )}
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Información del cálculo */}
                    <Card className="border-0 shadow-sm rounded-xl mt-2">
                        <Card.Body className="p-4">
                            <Row>
                                <Col md={6}>
                                    <h6 className="font-bold text-gray-800 flex items-center gap-1">
                                        Información del cálculo
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip>
                                                    Cada vez que guardas la configuración se crea una versión con
                                                    fecha de vigencia. Si eliges "aplicar solo desde hoy", los días
                                                    anteriores siguen calculándose con la configuración vieja; si
                                                    eliges "corregir también el histórico", la nueva configuración
                                                    cubre también los días ya registrados. Así, cada día del período
                                                    se calcula con la configuración que estaba vigente en ese momento.
                                                </Tooltip>
                                            }
                                        >
                                            <span className="material-icons-outlined text-sm text-gray-400 cursor-help">
                                                info
                                            </span>
                                        </OverlayTrigger>
                                    </h6>
                                    <p className="text-sm text-gray-500 mb-0">
                                        El consumo se calcula con base en la configuración hidráulica del sistema
                                        y el promedio de pulsos diarios registrados en el periodo seleccionado.
                                    </p>
                                </Col>
                                <Col md={6}>
                                    <div className="text-sm text-gray-700 space-y-1">
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Camas en el bloque</span>
                                            <span className="font-semibold">{config.bedsPerLot.toLocaleString('es-CO')} camas</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Duración del pulso</span>
                                            <span className="font-semibold">{config.pulseDurationMinutes.toLocaleString('es-CO')} minutos</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Caudal por gotero</span>
                                            <span className="font-semibold">{config.flowRatePerDripperCcMin.toLocaleString('es-CO')} cc/min</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Goteros por metro</span>
                                            <span className="font-semibold">{config.drippersPerMeter.toLocaleString('es-CO')} goteros/m</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Longitud de cada cama</span>
                                            <span className="font-semibold">{config.bedLengthMeters.toLocaleString('es-CO')} m</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Líneas de goteo por cama</span>
                                            <span className="font-semibold">{config.dripLinesPerBed} líneas</span>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <p className="text-xs text-gray-400 mt-1 mb-0">
                        Los datos mostrados son estimaciones y pueden variar según las condiciones reales del sistema de riego.
                    </p>
                </Col>

                {/* Resumen del período */}
                <Col lg={4}>
                    <Card className="border-0 shadow-sm rounded-xl">
                        <Card.Header className="bg-white border-b border-gray-100 py-3">
                            <h5 className="mb-0 text-gray-800 font-bold">Resumen del período</h5>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <div className="flex items-start gap-3 py-2 border-bottom">
                                <span className="material-icons-outlined text-gray-400">event</span>
                                <div className="flex-1">
                                    <div className="text-xs text-gray-400">Período</div>
                                    <div className="text-sm font-bold text-gray-800">
                                        {formatDateEs(parseDateInputValue(rangeStart))} - {formatDateEs(parseDateInputValue(rangeEnd))}
                                    </div>
                                    <div className="text-[11px] text-gray-400">{periodDays} días</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 py-2 border-bottom">
                                <span className="material-icons-outlined text-blue-400">water_drop</span>
                                <div className="flex-1">
                                    <div className="text-xs text-gray-400">Consumo diario (promedio)</div>
                                    <div className="text-sm font-bold text-blue-700">{formatNumber(avgDailyLiters)} L/día</div>
                                    <div className="text-[11px] text-gray-400">{formatDecimal(avgDailyLiters / LITERS_PER_M3)} m³/día</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 py-2 border-bottom">
                                <span className="material-icons-outlined text-gray-400">calendar_month</span>
                                <div className="flex-1">
                                    <div className="text-xs text-gray-400">Consumo semanal (promedio)</div>
                                    <div className="text-sm font-bold text-blue-700">{formatNumber(avgWeeklyLiters)} L/semana</div>
                                    <div className="text-[11px] text-gray-400">{formatDecimal(avgWeeklyLiters / LITERS_PER_M3)} m³/semana</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 py-2 border-bottom">
                                <span className="material-icons-outlined text-gray-400">calendar_month</span>
                                <div className="flex-1">
                                    <div className="text-xs text-gray-400">Consumo mensual (promedio)</div>
                                    <div className="text-sm font-bold text-blue-700">{formatNumber(avgMonthlyLiters)} L/mes</div>
                                    <div className="text-[11px] text-gray-400">{formatDecimal(avgMonthlyLiters / LITERS_PER_M3)} m³/mes</div>
                                </div>
                            </div>

                            <div className="bg-green-50 border border-green-200 rounded-xl p-3 mt-3">
                                <div className="flex items-center gap-2 text-green-700 text-xs font-bold uppercase">
                                    <span className="material-icons-outlined text-base">eco</span>
                                    Consumo total del período
                                </div>
                                <div className="text-xl font-bold text-green-800 mt-1">{formatNumber(periodTotalLiters)} L</div>
                                <div className="text-xs text-green-600">{formatDecimal(periodTotalLiters / LITERS_PER_M3)} m³</div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {renderConfigModal()}
            {renderApplyChoiceModal()}
            {renderSaveToast()}
        </div>
    );
};

export default WaterConsumption;
